import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Mention,
  MentionContent,
  MentionInput,
  MentionItem,
} from "ui/components/mention";
import { Textarea } from "ui/components/textarea";
import { RenderProfile } from "components/render-profile";
import { getItemsFromCollection } from "directus/src/operations";
import { searchName } from "@/api/utils";

const TRIGGER = "@";
const SEARCH_DEBOUNCE_MS = 250;
const EMPLOYEE_FIELDS = ["id", "first_name", "last_name", "email", "avatar"];

const getLabel = (user) =>
  `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();

export default function EmployeeMention({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  onMentionSelect,
  onMentionsChange,
}) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mentionValue, setMentionValue] = useState([]);

  const inputRef = useRef(null);
  // label -> employee id, for every employee we've ever shown. The visible list
  // changes on each search, so it can't be used to resolve a selected mention.
  const userCacheRef = useRef(new Map());
  const lastTermRef = useRef(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);

  const fetchUsers = useCallback(async (term) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const statusFilter = { user: { status: { _eq: "active" } } };
      const nameFilter = term?.trim() ? searchName(term) : null;

      const response = await getItemsFromCollection("employees", {
        fields: EMPLOYEE_FIELDS,
        filter: nameFilter ? { _and: [statusFilter, nameFilter] } : statusFilter,
        sort: ["first_name"],
        limit: 10,
      });

      // Ignore responses that arrive after a newer search was started
      if (requestId !== requestIdRef.current) return;

      const list = response || [];
      list.forEach((user) => userCacheRef.current.set(getLabel(user), user.id));
      setUsers(list);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error searching employees:", error);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, []);

  // Debounced, and skipped when the term hasn't changed. The Mention primitive
  // calls onFilter once per item on every re-filter, so without this guard each
  // result set would trigger yet another request.
  const requestSearch = useCallback(
    (term) => {
      const normalized = (term || "").trim();
      if (normalized === lastTermRef.current) return;
      lastTermRef.current = normalized;

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(
        () => fetchUsers(normalized),
        SEARCH_DEBOUNCE_MS,
      );
    },
    [fetchUsers],
  );

  useEffect(() => {
    lastTermRef.current = "";
    fetchUsers("");
    return () => clearTimeout(debounceRef.current);
  }, [fetchUsers]);

  // The Mention primitive only computes item visibility while the user types,
  // so results that arrive later stay hidden (blank popup) until the next
  // keystroke. Once the new items have registered, make it re-evaluate. Its
  // input onFocus handler runs the same update as typing, without moving the
  // caret. Child layout effects (item registration) run before this one.
  // It runs twice because if the previous pass found 0 items, the primitive
  // refuses to open before re-counting. The first pass re-counts and the
  // second reopens the popup.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input || document.activeElement !== input || users.length === 0) return;
    input.dispatchEvent(new FocusEvent("focusin", { bubbles: true }));
    input.dispatchEvent(new FocusEvent("focusin", { bubbles: true }));
  }, [users]);

  // Filtering happens on the server, so show every option we have
  const handleFilter = useCallback(
    (options, term) => {
      requestSearch(term);
      return options;
    },
    [requestSearch],
  );

  const handleInputValueChange = (newValue) => {
    onChange?.({ target: { value: newValue } });

    // The primitive skips onFilter for an empty term, so reset the list
    // ourselves when the user is right after a bare "@"
    const cursor = inputRef.current?.selectionStart ?? newValue.length;
    if (/(^|\s)@$/.test(newValue.slice(0, cursor))) requestSearch("");
  };

  const handleValueChange = (labels) => {
    const added = labels.filter((label) => !mentionValue.includes(label));
    added.forEach((label) => {
      const id = userCacheRef.current.get(label);
      if (id) onMentionSelect?.({ id, label });
    });

    setMentionValue(labels);
    onMentionsChange?.(labels);
  };

  return (
    <Mention
      value={mentionValue}
      onValueChange={handleValueChange}
      inputValue={value ?? ""}
      onInputValueChange={handleInputValueChange}
      trigger={TRIGGER}
      onFilter={handleFilter}
      className={className}
      disabled={disabled}
    >
      <MentionInput placeholder={placeholder} asChild>
        <Textarea ref={inputRef} className={`min-h-32 ${className || ""}`} />
      </MentionInput>
      <MentionContent className="max-h-72 overflow-y-auto">
        {/* Keep existing items mounted while loading. If the list empties,
            the primitive closes the popup and resets keyboard highlight. */}
        {users.map((user) => {
          const label = getLabel(user);
          return (
            <MentionItem key={user.id} value={label} label={label}>
              <RenderProfile user={user} />
            </MentionItem>
          );
        })}
        {isLoading && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            Searching employees...
          </div>
        )}
      </MentionContent>
    </Mention>
  );
}
